import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Header } from "@/components/Header";
import { GiveawayRaffleCard } from "@/components/giveaways/GiveawayRaffleCard";
import { GiveawayReplay } from "@/components/giveaways/GiveawayReplay";
import { GiveawayRules } from "@/components/giveaways/GiveawayRules";
import { GiveawayWatchDrawing } from "@/components/giveaways/GiveawayWatchDrawing";
import { GiveawaysPageHero } from "@/components/giveaways/GiveawaysPageHero";
import {
  callEnterGiveawayRaffle,
  useActiveGiveawayRaffles,
  usePreviousGiveawayRaffle,
  useUserGiveawayRaffleEntries,
} from "@/lib/firebase";
import { useAuth } from "@/lib/auth";
import {
  MOCK_PREVIOUS_WEEKLY,
  MOCK_RAFFLES,
} from "@/lib/giveaways/mock";
import { loadStoredEntry, saveStoredEntry } from "@/lib/giveaways/storage";
import {
  CADENCES,
  type GiveawayRaffle,
  type GiveawayRaffleCadence,
  type GiveawayRaffleEntry,
} from "@/lib/giveaways/types";

export const Route = createFileRoute("/giveaways")({
  head: () => ({
    meta: [
      { title: "Giveaways — Real Pranks" },
      {
        name: "description",
        content:
          "Enter weekly, monthly, and quarterly giveaways. Win free membership, coupons, and VIP access.",
      },
      { property: "og:title", content: "Giveaways — Real Pranks" },
      {
        property: "og:description",
        content:
          "Three raffles running at once — open weekly entry plus members-only monthly and quarterly prizes.",
      },
    ],
  }),
  component: GiveawaysPage,
});

function GiveawaysPage() {
  const { user, profile, signedIn, isSubscriber } = useAuth();
  const { raffles: liveRaffles, loading } = useActiveGiveawayRaffles();
  const { byCadence: userEntriesByCadence } = useUserGiveawayRaffleEntries(
    signedIn ? user?.uid : undefined,
  );

  const accountName = useMemo(
    () => (profile?.name ?? user?.displayName ?? "").trim(),
    [profile?.name, user?.displayName],
  );

  const displayRaffles = useMemo(() => {
    const merged = { ...MOCK_RAFFLES };
    if (!loading) {
      for (const cadence of CADENCES) {
        if (liveRaffles[cadence]) merged[cadence] = liveRaffles[cadence]!;
      }
    }
    return merged;
  }, [liveRaffles, loading]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submittingCadence, setSubmittingCadence] =
    useState<GiveawayRaffleCadence | null>(null);
  const [entries, setEntries] = useState<
    Partial<Record<GiveawayRaffleCadence, GiveawayRaffleEntry>>
  >({});

  useEffect(() => {
    if (signedIn && accountName) setName((n) => n || accountName);
    if (user?.email) setEmail((e) => e || user.email || "");
  }, [signedIn, accountName, user?.email]);

  useEffect(() => {
    const loaded: Partial<Record<GiveawayRaffleCadence, GiveawayRaffleEntry>> =
      {};
    const accountEmail = user?.email?.trim().toLowerCase() ?? "";
    for (const cadence of CADENCES) {
      const firestoreCurrent = userEntriesByCadence[cadence]?.current;
      if (firestoreCurrent) {
        loaded[cadence] = {
          raffleId: firestoreCurrent.raffleId,
          name: name.trim() || accountName,
          email: accountEmail || email.trim().toLowerCase(),
          entryCode: firestoreCurrent.entryCode,
        };
        continue;
      }
      const raffle = displayRaffles[cadence];
      if (raffle) {
        const stored = loadStoredEntry(raffle.id);
        if (stored) loaded[cadence] = stored;
      }
    }
    setEntries(loaded);
  }, [
    displayRaffles.weekly?.id,
    displayRaffles.monthly?.id,
    displayRaffles.quarterly?.id,
    userEntriesByCadence,
    user?.email,
    accountName,
    name,
    email,
  ]);

  const handleSubmit = useCallback(
    (cadence: GiveawayRaffleCadence) => async (e: React.FormEvent) => {
      e.preventDefault();
      const raffle = displayRaffles[cadence];
      if (!raffle) return;

      setSubmittingCadence(cadence);
      const submitEmail =
        signedIn && user?.email ? user.email.trim().toLowerCase() : email.trim();
      const result = await callEnterGiveawayRaffle({
        name: name.trim(),
        email: submitEmail,
        cadence,
        raffleId: liveRaffles[cadence] ? raffle.id : undefined,
      });
      setSubmittingCadence(null);

      if ("error" in result) {
        toast.error(result.error);
        return;
      }

      const newEntry: GiveawayRaffleEntry = {
        raffleId: result.raffleId,
        name: name.trim(),
        email: submitEmail,
        entryCode: result.entryCode,
      };
      saveStoredEntry(newEntry);
      setEntries((prev) => ({ ...prev, [cadence]: newEntry }));
      toast.success("You're entered! Save your entry code.");
    },
    [displayRaffles, liveRaffles, name, email, signedIn, user?.email],
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <GiveawaysPageHero />

        <section id="enter" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-16">
          <div className="grid gap-6 lg:grid-cols-3">
            {CADENCES.map((cadence) => {
              const raffle = displayRaffles[cadence];
              if (!raffle) return null;
              return (
                <GiveawayRaffleCard
                  key={cadence}
                  raffle={raffle}
                  name={name}
                  email={signedIn && user?.email ? user.email : email}
                  onNameChange={setName}
                  onEmailChange={setEmail}
                  onSubmit={handleSubmit(cadence)}
                  submitting={submittingCadence === cadence}
                  entry={entries[cadence] ?? null}
                  signedIn={signedIn}
                  isSubscriber={isSubscriber}
                />
              );
            })}
          </div>
        </section>

        <GiveawayRules />

        <section className="mx-auto max-w-7xl px-4 py-16">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold">Watch the drawings</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Live slot-machine reveals at drawing time for each raffle.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-3">
            {CADENCES.map((cadence) => (
              <CadenceWatchBlock
                key={cadence}
                cadence={cadence}
                raffle={displayRaffles[cadence]}
                entryCode={entries[cadence]?.entryCode}
              />
            ))}
          </div>
        </section>

        <section className="border-t border-border bg-card/30">
          <div className="mx-auto max-w-7xl px-4 py-16">
            <h2 className="mb-8 text-center text-3xl font-bold">
              Replay last drawings
            </h2>
            <div className="grid gap-8 lg:grid-cols-3">
              {CADENCES.map((cadence) => (
                <CadenceReplayBlock key={cadence} cadence={cadence} />
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function CadenceWatchBlock({
  cadence,
  raffle,
  entryCode,
}: {
  cadence: GiveawayRaffleCadence;
  raffle: GiveawayRaffle;
  entryCode?: string;
}) {
  return (
    <div>
      <GiveawayWatchDrawing
        raffle={raffle}
        userCode={entryCode}
        compact
        cadence={cadence}
      />
    </div>
  );
}

function CadenceReplayBlock({ cadence }: { cadence: GiveawayRaffleCadence }) {
  const { raffle: previous } = usePreviousGiveawayRaffle(cadence);
  const replay =
    previous ??
    (cadence === "weekly" ? MOCK_PREVIOUS_WEEKLY : null);
  return <GiveawayReplay raffle={replay} compact cadence={cadence} />;
}
